import type { ListBuilderConfiguration } from '../types';

/**
 * PD-16827 — the client account's order-time license copies: one row per order
 * whose license evidence was copied onto it when it was placed. Shown in the
 * Licenses tab's "Download licensing" dialog; the order-date filter picks which
 * order copies the download includes.
 *
 * Data source: `GET /api/clients/:id/licenses/order-copies` (page, limit,
 * filters, sortFields). Filter keys: libs/core/src/filters/config/license-order-copies.ts.
 */
export const LICENSE_ORDER_COPY_CLIENT_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-license-order-copy-client-embedded',
			cacheTTL: 300000,
			name: 'Order-time License Copies',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'order-number-column',
					fields: [
						{
							id: 'order-number-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'orderNumber', objectTable: 'order' },
							},
							displayStyle: 'text',
							styleConfig: { size: 'sm', weight: 'medium', colorRule: { type: 'default' } },
						},
					],
					headers: [{ id: 'h-order-number', text: 'Order' }],
					sortable: false,
					width: 140,
				},
				{
					id: 'order-date-column',
					fields: [
						{
							id: 'order-date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'orderDate', objectTable: 'order' },
							},
							displayStyle: 'date',
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { dateFormat: 'mdy' },
							},
						},
					],
					headers: [{ id: 'h-order-date', text: 'Order Date' }],
					sortable: true,
					sortFields: [{ direction: 'DESC', fieldName: 'orderDate', objectTable: 'order' }],
					width: 130,
				},
				{
					id: 'stamped-at-column',
					fields: [
						{
							id: 'stamped-at-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'stampedAt', objectTable: 'order' },
							},
							displayStyle: 'date',
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { dateFormat: 'datetime-medium' },
							},
						},
					],
					headers: [{ id: 'h-stamped-at', text: 'Copied At' }],
					sortable: true,
					sortFields: [{ direction: 'DESC', fieldName: 'stampedAt', objectTable: 'order' }],
					width: 180,
				},
				{
					id: 'license-count-column',
					fields: [
						{
							id: 'license-count-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'licenseCount', objectTable: 'order' },
							},
							displayStyle: 'number',
							// Whole numbers: a number field defaults to two decimals ("2.00").
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { decimalPlaces: 0 },
							},
						},
					],
					headers: [{ id: 'h-license-count', text: 'Licenses' }],
					sortable: false,
					width: 100,
				},
				{
					id: 'evidence-count-column',
					fields: [
						{
							id: 'evidence-count-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'evidenceCount', objectTable: 'order' },
							},
							displayStyle: 'number',
							// Whole numbers: a number field defaults to two decimals ("2.00").
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { decimalPlaces: 0 },
							},
						},
					],
					headers: [{ id: 'h-evidence-count', text: 'Evidence Files' }],
					sortable: false,
					width: 120,
				},
			],
			defaultSortId: 'order-date-desc',
		},
	],
	tableSorts: [
		{
			id: 'order-date-desc',
			name: 'Newest order first',
			fields: [{ direction: 'DESC', fieldName: 'orderDate', objectTable: 'order' }],
		},
		{
			id: 'order-date-asc',
			name: 'Oldest order first',
			fields: [{ direction: 'ASC', fieldName: 'orderDate', objectTable: 'order' }],
		},
	],
	paging: {
		defaultPageSize: 10,
		pageSizeOptions: [10, 25, 50],
	},
	showResetButton: true,
	filters: {
		activeFilters: ['licenseOrderCopyDateRange'],
	},
	objects: [
		{
			baseUrl: '/clients/:id/licenses/order-copies',
			objectLabel: 'Order-time License Copy',
			objectTable: 'order',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'orderNumber', fieldLabel: 'Order' },
				{ fieldName: 'orderDate', fieldLabel: 'Order Date' },
				{ fieldName: 'stampedAt', fieldLabel: 'Copied At' },
				{ fieldName: 'licenseCount', fieldLabel: 'Licenses' },
				{ fieldName: 'evidenceCount', fieldLabel: 'Evidence Files' },
			],
		},
	],
};
